import React, { useState } from "react";
import { useTranslation } from "locales";
import "./style.scss";

interface PinnedItem {
  messageId: string;
  text?: string;
  senderName?: string;
}

interface Props {
  pinnedMessages: PinnedItem[];
  onScrollToMessage: (messageId: string) => void;
  onUnpin: (messageId: string) => void;
  canUnpin: boolean;
}

function PinnedMessagesBar({ pinnedMessages, onScrollToMessage, onUnpin, canUnpin }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (pinnedMessages.length === 0) return null;

  const latest = pinnedMessages[pinnedMessages.length - 1];

  if (!expanded) {
    return (
      <div className="pinned-bar" onClick={() => pinnedMessages.length > 1 ? setExpanded(true) : onScrollToMessage(latest.messageId)}>
        <div className="pinned-bar__icon">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" fill="currentColor" />
          </svg>
        </div>
        <div className="pinned-bar__content">
          <div className="pinned-bar__label">
            {t("Pinned message")}
            {pinnedMessages.length > 1 && <span className="pinned-bar__count">{pinnedMessages.length}</span>}
          </div>
          <div className="pinned-bar__text">{latest.text || t("Attachment")}</div>
        </div>
        {pinnedMessages.length > 1 && (
          <button className="pinned-bar__expand" onClick={(e) => { e.stopPropagation(); setExpanded(true); }} type="button">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pinned-panel">
      <div className="pinned-panel__header">
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" fill="currentColor" />
        </svg>
        <span>{t("Pinned messages")} ({pinnedMessages.length})</span>
        <button className="pinned-panel__close" onClick={() => setExpanded(false)} type="button">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" fill="currentColor" />
          </svg>
        </button>
      </div>
      <div className="pinned-panel__list">
        {[...pinnedMessages].reverse().map((pin) => (
          <div key={pin.messageId} className="pinned-panel__item" onClick={() => { onScrollToMessage(pin.messageId); setExpanded(false); }}>
            <div className="pinned-panel__item-body">
              <div className="pinned-panel__item-sender">{pin.senderName || t("User")}</div>
              <div className="pinned-panel__item-text">{pin.text || t("Attachment")}</div>
            </div>
            {canUnpin && (
              <button
                className="pinned-panel__item-unpin"
                onClick={(e) => { e.stopPropagation(); onUnpin(pin.messageId); }}
                type="button"
                title={t("Unpin")}
              >
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default PinnedMessagesBar;
